/**
 * Fase 7 — Helpers puros consumidos por `Assinatura.tsx` para o fluxo de
 * top-up. Extraídos para permitir teste isolado (sem DOM, sem React Router).
 */

export interface TopupCheckoutBody {
  topupId: string;
  couponCode?: string;
}

export type TopupCheckoutOutcome =
  | { kind: 'redirect'; url: string }
  | { kind: 'invalid_coupon' }
  | { kind: 'coupon_already_redeemed' }
  | { kind: 'error'; message: string };

/**
 * Minimal interface that `performTopupCheckoutRequest` needs from the
 * API client — lets tests pass a mock sem depender do módulo real.
 * Compatível com `apiClient` de `lib/api.ts` que aceita `RequestInit`.
 */
export type ApiClientLike = (endpoint: string, options?: RequestInit) => Promise<Response>;

/**
 * Monta o body do POST `/billing/topup-checkout`. Omite `couponCode` quando
 * não fornecido ou quando vira string vazia após trim. Isso mantém o payload
 * mínimo e simples — o backend aceita `couponCode` opcional.
 */
export function buildTopupCheckoutBody(input: {
  topupId: string;
  couponCode?: string;
}): TopupCheckoutBody {
  const trimmed = input.couponCode?.trim();
  if (trimmed) {
    return { topupId: input.topupId, couponCode: trimmed };
  }
  return { topupId: input.topupId };
}

/**
 * Lê o deep link `?coupon=X` e retorna o valor normalizado ou `undefined`.
 * Faz trim; strings vazias após trim são tratadas como ausentes.
 */
export function parseCouponFromSearch(params: URLSearchParams): string | undefined {
  const raw = params.get('coupon');
  if (!raw) return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

/**
 * Dispara a chamada `POST /billing/topup-checkout` com o body
 * normalizado (via `buildTopupCheckoutBody`) e traduz o resultado em um
 * `TopupCheckoutOutcome` discriminado:
 *   - `redirect` → caller faz `window.location.href = url`
 *   - `invalid_coupon` → caller mostra toast dedicado de cupom inválido
 *   - `error` → caller mostra toast genérico
 *
 * Encapsular aqui permite testar o fluxo de sucesso/erro com um mock
 * simples de `ApiClientLike` (ver `topup-helpers.test.ts`).
 */
export async function performTopupCheckoutRequest(
  apiClient: ApiClientLike,
  input: { topupId: string; couponCode?: string }
): Promise<TopupCheckoutOutcome> {
  try {
    const body = buildTopupCheckoutBody(input);
    const res = await apiClient('/billing/topup-checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = (await res.json()) as { url?: string; code?: string; error?: string };
    if (!res.ok) {
      if (data.code === 'INVALID_COUPON') {
        return { kind: 'invalid_coupon' };
      }
      if (data.code === 'COUPON_ALREADY_REDEEMED') {
        return { kind: 'coupon_already_redeemed' };
      }
      return { kind: 'error', message: data.error || `HTTP ${res.status}` };
    }
    if (!data.url) {
      return { kind: 'error', message: 'URL não retornada pela API' };
    }
    return { kind: 'redirect', url: data.url };
  } catch (err) {
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'tente novamente',
    };
  }
}

/**
 * Threshold canônico — saldo de qualquer action type <= 1 é considerado
 * "baixo" e dispara o `GlobalLowCreditBanner`.
 */
export const LOW_CREDIT_THRESHOLD = 1;

/**
 * Pure helper: decide quais actionTypes devem dispara o banner de saldo
 * baixo. Retorna a lista filtrada (vazia quando não há nada a mostrar).
 *
 * - Se `totals` é null/undefined → sem banner.
 * - Se `actionTypes` vazia → sem banner.
 * - Caso contrário retorna os actionTypes cujo total <= LOW_CREDIT_THRESHOLD.
 *
 * Esse helper é testável sem DOM e centraliza a regra de exibição para
 * não duplicar em múltiplos componentes.
 */
export function pickLowActionTypes<T extends string>(
  balance: { actionTypes: readonly T[]; totals: Record<string, number> } | null | undefined,
  threshold: number = LOW_CREDIT_THRESHOLD
): T[] {
  if (!balance) return [];
  if (!balance.actionTypes || balance.actionTypes.length === 0) return [];
  return balance.actionTypes.filter((at) => (balance.totals[at] ?? 0) <= threshold);
}

/**
 * Ordem canônica de exibição dos action types na lista de top-ups.
 *
 * Agrupa por produto (correction primeiro, depois exam_creation,
 * student_correction, simulation_official), e dentro de cada grupo
 * ordena por `amount` crescente (ex.: 5 correções antes de 10).
 * Mantém uma apresentação estável — sem depender da ordem arbitrária
 * vinda do Firestore — e alinha com a hierarquia do produto:
 * correção (core B2B) > criação > correção de aluno > simulado de prova anterior.
 */
const TOPUP_ACTION_TYPE_ORDER: Record<string, number> = {
  correction: 0,
  exam_creation: 1,
  student_correction: 2,
  simulation_official: 3,
};

interface SortableTopup {
  id: string;
  actionType: string;
  amount: number;
  priceBRL: number;
}

export function sortTopupsForDisplay<T extends SortableTopup>(topups: readonly T[]): T[] {
  const rank = (at: string) =>
    at in TOPUP_ACTION_TYPE_ORDER ? TOPUP_ACTION_TYPE_ORDER[at] : Number.MAX_SAFE_INTEGER;
  return [...topups].sort((a, b) => {
    const rankDiff = rank(a.actionType) - rank(b.actionType);
    if (rankDiff !== 0) return rankDiff;
    const amountDiff = a.amount - b.amount;
    if (amountDiff !== 0) return amountDiff;
    return a.priceBRL - b.priceBRL;
  });
}
